import React from "react";
import { Calendar, Clock, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  badgeBase,
  statusStyles,
  cardTitle,
  microLabel,
  bodySecondary,
  iconSize,
  dividerInner,
  motionHoverLift,
} from "../../../../lib/design-system";
import { cn } from "../../../../lib/utils";
import { getCardColor } from "../../../../utils/courseHelpers";

const AssignmentCard = ({ assignment, courseID }) => {
  const sub = assignment.studentSubmission;
  const isPastDue = new Date(assignment.dueDate) < new Date();

  // --- Status badge ---
  const getStatus = () => {
    if (sub?.status === "graded")
      return { label: "Graded", style: statusStyles.submitted };
    if (sub?.status === "returned")
      return { label: "Returned", style: statusStyles.returned };
    if (sub)
      return { label: "Submitted", style: statusStyles.submitted };
    if (isPastDue)
      return {
        label: "Missing",
        style:
          "text-red-600 bg-red-50 border-red-200 dark:text-red-400 dark:bg-red-900/30 dark:border-red-700",
      };
    return { label: "Pending", style: statusStyles.in_progress };
  };

  const status = getStatus();

  // --- Question type summary ---
  const buildQuestionSummary = () => {
    const questions = assignment.questions || [];
    if (questions.length === 0) return null;

    const subjective = questions.filter((q) => q.type === "subjective").length;
    const objective = questions.filter((q) => q.type === "objective").length;

    const parts = [];
    if (subjective > 0) parts.push(`${subjective} Subjective`);
    if (objective > 0) parts.push(`${objective} MCQ`);

    const pts = assignment.totalPoints;
    if (pts) parts.push(`${pts} pts`);

    return parts.join(" \u00b7 ");
  };

  const questionSummary = buildQuestionSummary();

  // --- Score display ---
  const grade = sub?.grade;
  const isGraded = sub?.status === "graded";
  const totalPts = assignment.totalPoints;

  const getScoreColor = () => {
    if (!isGraded || grade == null || !totalPts) return "";
    const pct = (grade / totalPts) * 100;
    if (pct >= 75) return "text-emerald-600 dark:text-emerald-400";
    if (pct >= 50) return "text-amber-600 dark:text-amber-400";
    return "text-red-600 dark:text-red-400";
  };

  // --- Date formatting ---
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const accentColor = getCardColor(assignment.title);

  return (
    <motion.div whileHover={motionHoverLift} className="block">
    <Link
      to={`/student/course/${courseID}?section=Graded&assignment=${assignment._id}`}
      className="block group"
    >
      <div
        className={cn(
          "flex overflow-hidden rounded-sm border transition-all duration-200",
          "border-gray-200 dark:border-gray-700",
          "bg-white dark:bg-gray-800",
          "hover:shadow-md"
        )}
      >
        {/* Left color accent strip */}
        <div className="w-1.5 flex-shrink-0 transition-all duration-200 group-hover:w-2" style={{ backgroundColor: accentColor }} />

        <div className="flex-1 min-w-0 p-4">
          {/* Row 1: Title + Badge */}
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className={cn(cardTitle, "truncate")}>
                {assignment.title}
              </h3>
              {questionSummary && (
                <p className={cn(microLabel, "mt-0.5")}>{questionSummary}</p>
              )}
            </div>
            <span className={cn(badgeBase, status.style, "flex-shrink-0 whitespace-nowrap")}>
              {status.label}
            </span>
          </div>

          {/* Row 2: Description */}
          {assignment.description && (
            <p className={cn(bodySecondary, "mt-2 line-clamp-2")}>
              {assignment.description}
            </p>
          )}

          {/* Row 3: Footer */}
          <div className={cn("mt-3 pt-3 border-t flex items-center gap-4 flex-wrap", dividerInner)}>
            {/* Due date */}
            <span
              className={cn(
                "flex items-center gap-1 text-xs",
                isPastDue && !sub
                  ? "text-red-600 dark:text-red-400 font-medium"
                  : "text-gray-500 dark:text-gray-400"
              )}
            >
              <Calendar className={iconSize.xs} />
              {formatDate(assignment.dueDate)}
            </span>

            {/* Timer */}
            {assignment.completeIn && (
              <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                <Clock className={iconSize.xs} />
                {assignment.completeIn} min
              </span>
            )}

            {/* Score or Points */}
            {isGraded && grade != null ? (
              <span className={cn("text-xs font-medium", getScoreColor())}>
                Score: {grade}/{totalPts}
              </span>
            ) : assignment.isUngraded ? (
              <span className="text-xs text-gray-500 dark:text-gray-400">Ungraded</span>
            ) : totalPts ? (
              <span className="text-xs text-gray-500 dark:text-gray-400">{totalPts} pts</span>
            ) : null}

            {/* Arrow */}
            <ChevronRight
              className={cn(
                iconSize.md,
                "ml-auto text-gray-400 dark:text-gray-500 group-hover:text-accent1 transition-colors"
              )}
            />
          </div>
        </div>
      </div>
    </Link>
    </motion.div>
  );
};

export default AssignmentCard;
